import React from 'react';
import {
    Avatar,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Chip,
} from '@mui/material';
import { ChevronRight as ChevronRightIcon, SwipeLeft as SwipeLeftIcon } from '@mui/icons-material';

export type ShortFormTableSortKey = 'username' | 'channelName' | 'totalReels' | 'totalShorts' | 'totalViews' | 'totalLikes' | 'totalComments';

export interface ShortFormCreatorsTableRow {
    id: string;
    /** Resolved image URL for the avatar (parent should pass getS3Url/ensureDirectS3Url result or profilePicUrl). */
    avatarUrl?: string;
    /** Display label next to the avatar (e.g. @username or channel name). */
    label: string;
    /** Fallback character(s) shown in avatar when avatarUrl is missing (e.g. first letter of name). */
    avatarFallback?: string;
    count: number;
    views: number;
    likes: number;
    comments: number;
}

export interface ShortFormCreatorsTableProps {
    firstColumnLabel: string;
    countColumnLabel: string;
    nameSortKey: 'username' | 'channelName';
    countSortKey: 'totalReels' | 'totalShorts';
    orderBy: ShortFormTableSortKey;
    order: 'asc' | 'desc';
    onSort: (property: ShortFormTableSortKey) => void;
    rows: ShortFormCreatorsTableRow[];
    onRowClick: (id: string) => void;
    onRowMouseEnter?: (id: string) => void;
    isEnglish: boolean;
    formatNumber: (n: number) => string;
}

export function ShortFormCreatorsTable({
    firstColumnLabel,
    countColumnLabel,
    nameSortKey,
    countSortKey,
    orderBy,
    order,
    onSort,
    rows,
    onRowClick,
    onRowMouseEnter,
    isEnglish,
    formatNumber,
}: ShortFormCreatorsTableProps) {
    const commentsLabel = isEnglish ? 'Comments' : 'Comentarios';
    const swipeHint = isEnglish ? 'Swipe to see more' : 'Desliza para ver más';

    return (
        <TableContainer component={Paper} variant="outlined">
            <Box
                sx={{
                    display: { xs: 'flex', md: 'none' },
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    color: 'text.secondary',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <SwipeLeftIcon />
                <Typography variant="caption">{swipeHint}</Typography>
            </Box>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: { xs: '100px', sm: '150px' }, padding: { xs: '8px', sm: '16px' } }}>
                            <TableSortLabel
                                active={orderBy === nameSortKey}
                                direction={orderBy === nameSortKey ? order : 'asc'}
                                onClick={() => onSort(nameSortKey)}
                            >
                                {firstColumnLabel}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', padding: { xs: '8px', sm: '16px' } }}>
                            <TableSortLabel
                                active={orderBy === countSortKey}
                                direction={orderBy === countSortKey ? order : 'desc'}
                                onClick={() => onSort(countSortKey)}
                            >
                                {countColumnLabel}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', padding: { xs: '8px', sm: '16px' } }}>
                            <TableSortLabel
                                active={orderBy === 'totalViews'}
                                direction={orderBy === 'totalViews' ? order : 'desc'}
                                onClick={() => onSort('totalViews')}
                            >
                                Views
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                            <TableSortLabel
                                active={orderBy === 'totalLikes'}
                                direction={orderBy === 'totalLikes' ? order : 'desc'}
                                onClick={() => onSort('totalLikes')}
                            >
                                Likes
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                            <TableSortLabel
                                active={orderBy === 'totalComments'}
                                direction={orderBy === 'totalComments' ? order : 'desc'}
                                onClick={() => onSort('totalComments')}
                            >
                                {commentsLabel}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" sx={{ width: { xs: '24px', sm: '40px' }, padding: { xs: '8px 4px', sm: '16px' } }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow
                            key={row.id}
                            hover
                            onClick={() => onRowClick(row.id)}
                            onMouseEnter={onRowMouseEnter ? () => onRowMouseEnter(row.id) : undefined}
                            sx={{ cursor: 'pointer' }}
                        >
                            <TableCell sx={{ overflow: 'hidden', padding: { xs: '12px', sm: '16px' } }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                                    {row.avatarUrl ? (
                                        <Avatar src={row.avatarUrl} variant="rounded" sx={{ width: 32, height: 32 }} />
                                    ) : (
                                        <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'grey.300' }}>
                                            {row.avatarFallback ?? ''}
                                        </Avatar>
                                    )}
                                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: '4px' }}>
                                        {row.label}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', padding: { xs: '12px', sm: '16px' } }}>
                                <Chip label={row.count} size="small" />
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', padding: { xs: '12px', sm: '16px' } }}>
                                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
                                    {formatNumber(row.views)}
                                </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    {formatNumber(row.likes)}
                                </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    {formatNumber(row.comments)}
                                </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ width: { xs: '24px', sm: '40px' }, padding: { xs: '8px 4px', sm: '16px' } }}>
                                <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
